import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, X, Minus, Plus, Trash2 } from "lucide-react";
import { formatMoney } from "@/lib/currency";

const formatMoneyOrFree = (amount, currency) =>
  Number(amount || 0) === 0 ? "Free" : formatMoney(amount, currency);

export default function StorefrontCartButton({ username, currency, cart, stockMap = {} }) {
  const [open, setOpen] = useState(false);

  const availableQtyFor = (line) => {
    const stock = stockMap[line.catalog_item_id];
    if (!stock) return null; // unknown — don't block, server still validates at checkout
    const raw = line.size ? stock.stock_by_size?.[line.size] : stock.overall_qty;
    return raw === undefined || raw === null ? null : Number(raw);
  };

  return (
    <>
      {/* Fixed, always-visible launcher — stays pinned on desktop, mobile, and tablet */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-5 right-5 inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-4 py-2.5 text-sm font-semibold text-ink shadow-lg hover:bg-ink/5"
        style={{
          bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))",
          right: "calc(1.25rem + env(safe-area-inset-right, 0px))",
        }}
        data-testid="storefront-cart-button"
      >
        <ShoppingCart size={14} /> Cart
        {cart.count > 0 && (
          <span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-hotpink text-[10px] font-bold text-white">
            {cart.count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end bg-ink/40">
          <div
            className="flex h-full w-full max-w-sm flex-col bg-white p-6"
            style={{
              paddingTop: "calc(1.5rem + env(safe-area-inset-top, 0px))",
              paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))",
            }}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-black tracking-tight">Your cart</h3>
              <button onClick={() => setOpen(false)} className="rounded-full border border-ink/15 p-1.5 hover:bg-ink/5">
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 flex-1 space-y-4 overflow-y-auto">
              {cart.items.length === 0 ? (
                <p className="text-sm text-ink/50">Your cart is empty.</p>
              ) : (
                cart.items.map((line) => (
                  <div key={`${line.catalog_item_id}-${line.size || ""}`} className="flex gap-3">
                    <div className="h-16 w-16 flex-none overflow-hidden rounded-xl border border-ink/10 bg-ink/5">
                      {line.image_url && <img src={line.image_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink">{line.name}</p>
                      {line.size && <p className="text-xs text-ink/50">Size: {line.size}</p>}
                      <p className="mt-0.5 text-xs font-semibold text-ink/70">{formatMoneyOrFree(line.price, currency)}</p>
                      <div className="mt-1.5 flex items-center gap-2">
                        {!line.is_digital && (
                        <>
                        <button
                          type="button"
                          onClick={() => cart.updateQuantity(line.catalog_item_id, line.size, line.quantity - 1)}
                          className="grid h-6 w-6 place-items-center rounded-full border border-ink/15 hover:bg-ink/5"
                        >
                          <Minus size={11} />
                        </button>
                        <span className="w-4 text-center text-xs font-semibold">{line.quantity}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const available = availableQtyFor(line);
                            if (available !== null && line.quantity >= available) return;
                            cart.updateQuantity(line.catalog_item_id, line.size, line.quantity + 1);
                          }}
                          disabled={(() => {
                            const available = availableQtyFor(line);
                            return available !== null && line.quantity >= available;
                          })()}
                          className="grid h-6 w-6 place-items-center rounded-full border border-ink/15 hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <Plus size={11} />
                        </button>
                        </>
                        )}
                        {line.is_digital && (
                          <span className="rounded-full bg-ink/5 px-2 py-0.5 text-[10px] font-semibold text-ink/60">Digital</span>
                        )}
                        <button
                          type="button"
                          onClick={() => cart.removeItem(line.catalog_item_id, line.size)}
                          className="ml-1 text-ink/40 hover:text-hotpink"
                          aria-label="Remove"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                      {(() => {
                        const available = availableQtyFor(line);
                        return available !== null && line.quantity >= available ? (
                          <p className="mt-1 text-[10px] font-semibold text-hotpink">Max available in stock</p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.items.length > 0 && (
              <div className="mt-5 border-t border-ink/10 pt-4">
                <div className="flex items-center justify-between text-sm font-semibold text-ink">
                  <span>Subtotal</span>
                  <span>{formatMoneyOrFree(cart.subtotal, currency)}</span>
                </div>
                <p className="mt-1 text-xs text-ink/40">Shipping & discounts calculated at checkout.</p>
                <Link
                  to={`/store/${username}/checkout`}
                  onClick={() => setOpen(false)}
                  className="mt-4 block rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-3 text-center text-sm font-semibold text-white hover:opacity-95"
                >
                  Checkout
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
